/** Lifetime free-tier file cap. Free users get 5 total redactions across
 *  all sessions before they need to go Pro. */
export const FREE_LIFETIME_LIMIT = 5;

/** Free-tier per-file size cap in bytes (10 MB). Pro raises this to 100 MB. */
export const FREE_FILE_BYTES = 10 * 1024 * 1024;
export const PRO_FILE_BYTES = 100 * 1024 * 1024;

/** Free-tier batch cap. Anything over this requires Pro. */
export const FREE_BATCH_FILES = 3;

const STORAGE_KEY = 'anomia:free-tier';

type Counter = { count: number };

const read = (): Counter => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { count: 0 };
    const parsed = JSON.parse(raw) as { count?: unknown; date?: unknown };
    // Forward-compat: previous schema was { date, count } and reset
    // daily. Drop the date and keep the count so existing free users
    // don't get a fresh allowance just because they were on the
    // daily-cap version. count is clamped to [0, FREE_LIFETIME_LIMIT].
    if (typeof parsed.count === 'number') {
      return {
        count: Math.max(0, Math.min(FREE_LIFETIME_LIMIT, Math.floor(parsed.count))),
      };
    }
  } catch {
    /* ignore */
  }
  return { count: 0 };
};

const write = (counter: Counter): void => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(counter));
  } catch {
    /* localStorage unavailable */
  }
};

/** Total free redactions used so far (lifetime). */
export const usedTotal = (): number => read().count;

/** Number of free redactions remaining before Pro is required. */
export const remainingFree = (): number => Math.max(0, FREE_LIFETIME_LIMIT - read().count);

/** True if the user can run another redaction without going Pro. */
export const canRedactNow = (cost = 1): boolean => {
  const c = read();
  return c.count + cost <= FREE_LIFETIME_LIMIT;
};

/**
 * Record one or more successful redactions against the lifetime
 * counter. No-op if the cost would push past the limit (caller should
 * gate first).
 */
export const recordRedaction = (cost = 1): void => {
  const c = read();
  // Clamp at LIMIT so the stored counter can never exceed the cap.
  // The gate (canRedactNow) should already have stopped the redaction
  // before we got here; this is defence-in-depth against a future
  // caller that forgets to gate.
  write({ count: Math.min(FREE_LIFETIME_LIMIT, c.count + cost) });
};

/** Reset for tests. */
export const _reset = (): void => {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
};
